import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Answer, Question } from 'types';
import { ChangeEvent, useEffect, useState } from 'react';
import { QuestionType } from 'consts';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import remove from 'lodash/remove';
import cloneDeep from 'lodash/cloneDeep';
import { CdnUtils } from 'utils';
import { Spinner } from './spinner';

interface Props {
    question?: Question | null;
    onSaveQuestion: (question: Question) => void;
}

const defaultQuestion: Question = {
    genreId: 1,
    questionTypeId: QuestionType.Text,
    answers: [
        {
            isCorrect: true
        }
    ]
};

export const QuestionDetail = (props: Props) => {
    const MAX_MULTIPLE_CHOICE_ANSWERS = 4;
    const [question, setQuestion] = useState<Question>(cloneDeep(defaultQuestion));
    const [isMultipleChoice, setIsMultipleChoice] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const [contentFile, setContentFile] = useState<File>();

    useEffect(() => {
        if (props.question) {
            const newQuestion = cloneDeep(props.question);
            newQuestion.answers.forEach((a) => (a.key = crypto.randomUUID()));
            if (props.question.answers.length > 1) {
                setIsMultipleChoice(true);
            }
            setQuestion(newQuestion);
            return;
        }

        const newQuestion = cloneDeep(defaultQuestion);
        setQuestion(newQuestion);
    }, [props.question]);

    const onQuestionTypeChange = (event: SelectChangeEvent<number>) => {
        setQuestion({
            ...question,
            questionTypeId: event.target.value as number
        });
    };

    const onGenreChange = (event: SelectChangeEvent<number>) => {
        setQuestion({
            ...question,
            genreId: event.target.value as number
        });
    };

    const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const fieldName = event.target.getAttribute('data-field') as keyof Question;

        setQuestion({
            ...question,
            [fieldName]: event.target.value as string
        });
    };

    const onAnswerChange = (answerKey: string, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const answers = [...question.answers];
        const currentAnswer = answers.find((a) => a.key === answerKey)!;
        currentAnswer.answer = event.target.value as string;
        delete currentAnswer.id;

        setQuestion({
            ...question,
            answers
        });
    };

    const onToggleMultipleChoice = () => {
        const newMultipleChoice = !isMultipleChoice;
        const answer = question.answers.at(0)!;
        answer.isCorrect = true;
        setQuestion({
            ...question,
            answers: [answer]
        });
        setIsMultipleChoice(newMultipleChoice);
    };

    const onAddMultipleChoiceAnswer = () => {
        if (question.answers.length >= MAX_MULTIPLE_CHOICE_ANSWERS) {
            return;
        }

        const newAnswer: Answer = {
            key: crypto.randomUUID(),
            isCorrect: false
        };
        setQuestion({
            ...question,
            answers: [...question.answers, newAnswer]
        });
    };

    const onDeleteMultipleChoiceAnswer = (key: string) => {
        const answers = [...question.answers];
        remove(answers, (a) => a.key === key);

        setQuestion({
            ...question,
            answers
        });
    };

    const onAnswerCorrectChange = (key: string) => {
        const answers = [...question.answers];
        answers.forEach((a) => (a.isCorrect = false));
        const currentAnswer = answers.find((a) => a.key === key)!;
        currentAnswer.isCorrect = !currentAnswer?.isCorrect;

        setQuestion({
            ...question,
            answers
        });
    };

    const onClearContentFile = () => {
        setContentFile(undefined);
        setQuestion({
            ...question,
            contentUrl: undefined
        });
    };

    const onContentFileUpload: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (e.target.files) {
            setQuestion({
                ...question,
                contentUrl: e.target.files[0].name
            });
            setContentFile(e.target.files[0]);
        }
    };

    const onSaveQuestion = async () => {
        const questionToSave = cloneDeep(question);
        if (contentFile) {
            setShowSpinner(true);
            const cdn = new CdnUtils();
            const fileUrl = await cdn.uploadFile(contentFile);
            questionToSave.contentUrl = fileUrl;
            setShowSpinner(false);
        }
        props.onSaveQuestion(questionToSave);
        onCancelEdit();
    };

    const onCancelEdit = () => {
        setQuestion(cloneDeep(defaultQuestion));
        setContentFile(undefined);
        setIsMultipleChoice(false);
    };

    const isInputValid = (): boolean => {
        if (!question.question || !question.answers.filter((a) => a.answer).length || !question.answers.filter((a) => a.isCorrect).length) {
            return false;
        }
        return true;
    };

    const getAcceptedContentTypes = () => {
        switch (question.questionTypeId) {
            case QuestionType.Image:
                return 'image/*';
            case QuestionType.Video:
                return 'video/mp4,video/webm,video/ogg';
            case QuestionType.Audio:
                return 'audio/mp3';
            default:
                return '*';
        }
    };

    const getSupportedFileTypesText= () => {
        const supportedText = 'Supported file types for upload: ';
        switch (question.questionTypeId) {
            case QuestionType.Image:
                return `${supportedText}.jpg, .jpeg, .png, .gif`;
            case QuestionType.Video:
                return `${supportedText}.mp4, .webm, .ogv. Supported links to streaming platforms: YouTube, Vimeo, DailyMotion, Vidyard, and more.`;
            case QuestionType.Audio:
                return `${supportedText}.mp3. Supported links to streaming platforms: YouTube Music, SoundCloud, MixCloud, and more.`;
            default:
                return '';
        }
    }

    return (
        <Box sx={{ width: '80%' }}>
            <Typography variant="h4">{question.id ? 'Edit question' : 'Add new question'}</Typography>
            <Grid container spacing={2}>
                <Grid xs={12}>
                    <Typography variant="subtitle1">Question</Typography>
                </Grid>
                <Grid xs={6}>
                    {/* Question type */}
                    <FormControl fullWidth>
                        <InputLabel id="lbl-question-type">Question Type</InputLabel>
                        <Select
                            labelId="lbl-question-type"
                            id="ddl-question-type"
                            value={question.questionTypeId}
                            label="Question Type"
                            autoWidth
                            onChange={onQuestionTypeChange}
                        >
                            <MenuItem value={QuestionType.Text}>Text</MenuItem>
                            <MenuItem value={QuestionType.Image}>Image</MenuItem>
                            <MenuItem value={QuestionType.Audio}>Audio</MenuItem>
                            <MenuItem value={QuestionType.Video}>Video</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid xs={6}>
                    {/* Question genre */}
                    <FormControl fullWidth>
                        <InputLabel id="lbl-genre">Genre</InputLabel>
                        <Select
                            labelId="lbl-genre"
                            id="ddl-genre"
                            value={question.genreId ?? undefined}
                            label="Genre"
                            autoWidth
                            onChange={onGenreChange}
                        >
                            <MenuItem value={1}>SF</MenuItem>
                            <MenuItem value={2}>Fantasy</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid xs={12}>
                    {/* Question */}
                    <TextField
                        id="txt-question"
                        label="Question HTML"
                        multiline
                        fullWidth
                        rows={4}
                        onChange={onTextChange}
                        value={question.question ?? ''}
                        inputProps={{ 'data-field': 'question' }}
                        InputLabelProps={{ shrink: !!question.question }}
                    />
                </Grid>
                {question.questionTypeId !== QuestionType.Text && (
                    <>
                        <Grid xs={12}>
                            {/* Content URL */}
                            <TextField
                                id="txt-content-url"
                                label="Content URL"
                                variant="outlined"
                                fullWidth
                                onChange={onTextChange}
                                value={question.contentUrl ?? ''}
                                inputProps={{ 'data-field': 'contentUrl' }}
                                InputLabelProps={{ shrink: !!question.contentUrl }}
                                disabled={!!contentFile}
                            />
                        </Grid>
                        <Grid xs={12}>
                            <Stack direction="row" spacing={2}>
                                {/* Upload file */}
                                <Button variant="contained" component="label" htmlFor="input-file-upload-content">
                                    Upload file
                                </Button>
                                <input
                                    type="file"
                                    id="input-file-upload-content"
                                    hidden
                                    onChange={onContentFileUpload}
                                    accept={getAcceptedContentTypes()}
                                />
                                <Button variant="contained" onClick={onClearContentFile} disabled={!contentFile}>
                                    Remove file
                                </Button>
                                <Typography variant='caption'>
                                    {getSupportedFileTypesText()}
                                </Typography>
                            </Stack>
                        </Grid>
                    </>
                )}
                <hr style={{ width: '100%' }} />
                <Grid xs={12}>
                    <Typography variant="subtitle1">Answer</Typography>
                </Grid>
                <Grid xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                inputProps={{
                                    'aria-label': 'Is Answer Correct'
                                }}
                                onChange={onToggleMultipleChoice}
                                checked={isMultipleChoice}
                            />
                        }
                        label="Is Multiple Choice"
                    />
                </Grid>
                {isMultipleChoice ? (
                    <>
                        {question.answers.map((answer) => (
                            <React.Fragment key={`answer-fragment-${answer.key}`}>
                                <Grid xs={9} key={`answer-text-${answer.key}`}>
                                    <TextField
                                        id="txt-answer"
                                        label="Answer"
                                        fullWidth
                                        onChange={(e) => onAnswerChange(answer.key!, e)}
                                        variant="outlined"
                                        value={answer.answer ?? ''}
                                        inputProps={{ 'data-field': 'answer' }}
                                        InputLabelProps={{ shrink: !!answer.answer }}
                                    />
                                </Grid>
                                <Grid xs={2} key={`answer-correct-${answer.key}`}>
                                    {/* is correct answer */}
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                inputProps={{
                                                    'aria-label': 'Is Answer Correct'
                                                }}
                                                checked={answer.isCorrect}
                                                onChange={() => onAnswerCorrectChange(answer.key!)}
                                            />
                                        }
                                        label="Is correct"
                                    />
                                </Grid>
                                <Grid xs={1} key={`answer-delete-${answer.key}`}>
                                    <DeleteOutlineOutlinedIcon
                                        onClick={() => onDeleteMultipleChoiceAnswer(answer.key!)}
                                        sx={question.answers.length === 1 ? { display: 'none' } : {}}
                                    />
                                </Grid>
                            </React.Fragment>
                        ))}
                        {question.answers.length < MAX_MULTIPLE_CHOICE_ANSWERS && (
                            <Grid xs={12}>
                                <Button variant="contained" onClick={onAddMultipleChoiceAnswer}>
                                    Add Answer
                                </Button>
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        <Grid xs={12}>
                            {/* 1 Corrent answer */}
                            <TextField
                                id="txt-answer"
                                label="Answer"
                                fullWidth
                                multiline
                                rows={4}
                                onChange={(e) => onAnswerChange(question.answers.at(0)?.key!, e)}
                                variant="outlined"
                                value={question.answers[0].answer ?? ''}
                                inputProps={{ 'data-field': 'answer' }}
                                InputLabelProps={{ shrink: !!question.answers[0].answer }}
                            />
                        </Grid>
                    </>
                )}
                <Grid xs={12} alignContent={'end'}>
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" onClick={onSaveQuestion} disabled={!isInputValid()}>
                            Save Question
                        </Button>
                        {!!question.id && (
                            <Button variant="contained" onClick={onCancelEdit}>
                                Cancel Edit
                            </Button>
                        )}
                    </Stack>
                </Grid>
            </Grid>
            {showSpinner && <Spinner />}
        </Box>
    );
};
